<template>
  <div class="container max-width-container">
    <section class="section">
      <div class="columns is-multiline is-centered">
        <div class="column dashboard is-paddingless">
          <welcome-title v-if="$mq.desktop" :name="userDetailsState.first_name"></welcome-title>

          <div>
            <div v-if="!profileLoadingState && showCompleteProfileWarning" class="columns is-desktop">
              <div class="column is-3-desktop content-column balance-info-container">
                <profile-completeness-notification v-if="!$mq.desktop"></profile-completeness-notification>
                <welcome-title v-if="!$mq.desktop" :name="userDetailsState.first_name"></welcome-title>
                <balance-container></balance-container>
              </div>
              <div v-if="$mq.desktop" class="column is-7-desktop is-12-column content-column">
                <profile-completeness-notification></profile-completeness-notification>
              </div>
            </div>
            <div class="columns is-desktop">
              <div class="column is-3-desktop content-column">
                <div v-if="!profileLoadingState && !showCompleteProfileWarning" class="balance-info-container">
                  <welcome-title v-if="!$mq.desktop" :name="userDetailsState.first_name"></welcome-title>
                  <balance-container></balance-container>
                </div>
                <referral-link-container></referral-link-container>
                <referrals-panel-container v-if="$mq.phone && showReferralsPanel"></referrals-panel-container>
                <complete-profile-container v-if="isApplicant"></complete-profile-container>
                <looking-for-jobs-container v-if="$mq.desktop"></looking-for-jobs-container>
              </div>
              <div v-if="!$mq.phone && showReferralsPanel"
                   :class="{'is-6-desktop': !showApplicationsPanel, 'is-3-desktop': showApplicationsPanel}"
                   class="column content-column">
                <referrals-panel-container></referrals-panel-container>
              </div>
              <div class="column is-6-desktop content-column">
                <applications-container v-if="showApplicationsPanel"></applications-container>
                <recommended-jobs-container v-if="isApplicant"></recommended-jobs-container>
                <looking-for-jobs-container v-if="!$mq.desktop"></looking-for-jobs-container>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
<script>
import {mapGetters} from 'vuex';

import {find, isEmpty} from 'lodash';

import ReferralLinkContainer from '@/common/referral-link/ReferralLinkContainer';
import ProfileCompletenessNotification from '@/pages/dashboard/components/ProfileCompletenessNotification';
import WelcomeTitle from '@/pages/dashboard/components/WelcomeTitle';
import ApplicationsContainer from '@/pages/dashboard/panels/applications/ApplicationsContainer';
import CompleteProfileContainer from '@/pages/dashboard/panels/complete-profile/CompleteProfileContainer';
import LookingForJobsContainer from '@/pages/dashboard/panels/looking-for-jobs/LookingForJobsContainer';
import RecommendedJobsContainer from '@/pages/dashboard/panels/recommended-jobs/RecommendedJobsContainer';
import ReferralsPanelContainer from '@/pages/dashboard/panels/referrals/ReferralsPanelContainer';

export default {
  created() {
    this.$store.dispatch('getApplicationsAction').then(() => {
      if (this.$route.query['job_code']) {
        this.createApplicationByQueryParam(this.$route.query['job_code']).finally(() => {
          this.getRecommendedJobs();
        });
      } else {
        this.getRecommendedJobs();
      }
    });
    const profilePromise = this.getProfile();
    this.$store.dispatch('fetchReferralsAction');

    Promise.all([profilePromise, this.recommendedJobsPromise]).then(values => {
      this.scrollToAnchor();
      if (
        values[0] &&
        !values[0].is_top20tech_source &&
        values[0].basic_information.industry &&
        this.amountOfRecommendedJobs === 0
      ) {
        /* Needed to show the `Get job recommendations` button */
        this.$store.dispatch('getJobTitlesByIndustryAction', values[0].basic_information.industry);
      }
    });
  },
  mounted() {
    if (this.previousAnchorState) {
      if (this.previousAnchorState === this.jobsContainerIdState && !this.$route.query['job_code']) {
        this.$toast.open({message: this.$gettext('Applied successfully!'), type: 'is-success', duration: 3000});
        this.$store.commit('mutatePreviousAnchorState', null);
      }
    }
  },
  data() {
    let recommendedJobsPromiseResolve = null;
    const p = new Promise((resolve, reject) => {
      recommendedJobsPromiseResolve = resolve;
    });
    return {
      recommendedJobsPromise: p,
      recommendedJobsPromiseResolve: recommendedJobsPromiseResolve
    };
  },
  computed: {
    ...mapGetters([
      'userDetailsState',
      'featureSwitchesState',
      'applicationsListState',
      'allReferralsListState',
      'previousAnchorState',
      'jobsContainerIdState',
      'profileDetailsState',
      'profileLoadingState',
      'isProfilePublished',
      'amountOfRecommendedJobs'
    ]),
    isApplicant() {
      return this.profileDetailsState.is_applicant;
    },
    showApplicationsPanel() {
      return this.applicationsListState.length || this.isApplicant;
    },
    showReferralsPanel() {
      return this.allReferralsListState.length || (!isEmpty(this.profileDetailsState) && !this.isApplicant);
    },
    showCompleteProfileWarning() {
      return (
        !isEmpty(this.profileDetailsState) &&
        !this.profileDetailsState.all_required_filled &&
        !this.isProfilePublished &&
        this.isApplicant
      );
    }
  },
  methods: {
    createApplicationByQueryParam(applyCode) {
      if (find(this.applicationsListState, {code: applyCode})) {
        this.$toast.open({
          message: this.$gettext('You applied to this job already'),
          type: 'is-success',
          duration: 3000
        });
        this.$router.push({query: {}});
        return Promise.resolve();
      }

      return this.$store
        .dispatch('createJobApplicationAction', {code: applyCode, query: this.$route.query})
        .then(() => {
          this.$toast.open({message: this.$gettext('Applied successfully!'), type: 'is-success', duration: 3000});
        })
        .catch(error => {
          this.$toast.open({message: error, type: 'is-danger', duration: 3000});
        })
        .finally(() => {
          this.$router.push({query: {}});
        });
    },
    getRecommendedJobs() {
      let actionName = this.amountOfRecommendedJobs
        ? 'getRecommendedJobsListInBackgroundAction'
        : 'getRecommendedJobsListAction';
      this.$store.dispatch(actionName).then(data => this.recommendedJobsPromiseResolve(data));
    },
    getProfile() {
      let actionName = isEmpty(this.profileDetailsState) ? 'getProfileAction' : 'getProfileInBackgroundAction';
      return this.$store.dispatch(actionName);
    },
    scrollToAnchor() {
      if (this.$mq.desktop) {
        return;
      }
      const previousAnchor = this.$store.getters.previousAnchorState;
      const element = previousAnchor ? document.getElementById(previousAnchor) : null;
      if (element) {
        this.$scrollTo(element);
        this.$store.commit('mutatePreviousAnchorState', null);
      }
    }
  },
  components: {
    RecommendedJobsContainer,
    ApplicationsContainer,
    CompleteProfileContainer,
    ReferralsPanelContainer,
    ReferralLinkContainer,
    LookingForJobsContainer,
    WelcomeTitle,
    ProfileCompletenessNotification
  }
};
</script>

<style lang="scss" scoped>
@import '~VariablesStyles';
.dashboard {
  max-width: 100%;

  .welcome-heading {
    margin-bottom: 40px;
  }

  .columns .column .card-panel {
    margin-bottom: 40px;
  }

  .column.balance-info-container,
  .balance-info-container {
    padding-bottom: 40px;
  }
}

@media screen and (max-width: $tablet) {
  .dashboard {
    .column {
      padding: 0;
    }
  }
}

@media screen and (min-width: $tablet) {
  .container {
    padding-bottom: 100px;
  }

  .dashboard {
    .column {
      padding: 0 10px;
      &:first-child {
        margin-left: 0;
      }
      &:last-child {
        margin-right: 0;
      }

      &.is-3-desktop /deep/ .people {
        margin: 20px auto;
        max-width: 225px;
        max-height: 143px;
      }
    }
  }
}

@media screen and (min-width: $tablet) and (max-width: 1023px) {
  .dashboard {
    .welcome-heading {
      text-align: center;
    }
  }
}

@media screen and (min-width: $tablet) and (max-width: 1087px) {
  .dashboard {
    .is-12-column {
      width: 100%;
    }
  }
}

@media screen and (min-width: $desktop) and (max-width: 1087px) {
  .dashboard {
    .columns.is-desktop {
      display: -webkit-box;
      display: -ms-flexbox;
      display: flex;
    }

    .column {
      &.is-3-desktop,
      &.is-6-desktop,
      &.is-7-desktop {
        flex: none;
      }

      &.is-3-desktop {
        width: 25%;
      }

      &.is-6-desktop {
        width: 50%;
      }

      &.is-7-desktop {
        width: 58.33333%;
      }
    }
  }
}
</style>
